import { BausteinKarussell } from "../components/ui";
import React from "react";
import {
    AboutTeaserSection,
    AssistantSection,
    BlogCarouselSection,
    ChapterSection,
    ClosingCtaSection,
    ContactSection,
    CornerstonesSection,
    Footer,
    Header,
    HeroSection,
    HowWeWorkSection,
    MatTeaserBadge,
    PlattformNutzenSection,
    PortfolioCarouselSection,
    ProblemSection,
    RelatedStoriesSection,
    ThoughtLeadershipSection,
    SuccessStorySection,
    TrustSection,
} from "../components";
import { useMat3dIsland, useMatExposure } from "../components/sections/mat3d";
import { VpStickySectionNav } from "../components/vp-overview/VpStickySectionNav";
import { type HomepageProps } from "../content/page-homepage";
import { type SectionMatTeaserProps } from "../content/section-mat-teaser";
import { useContent, useHead, useLocalizedUrl } from "../hooks";
import { useLocation } from "react-router-dom";

/**
 * Die sechs Kapitel der Sprungnavigation, in Seitenreihenfolge — und zugleich
 * die Kapitel-Etiketten (12.8.2026).
 *
 * Hero und Assistent fehlen mit Absicht: Sie sind der Einstieg, kein Sprungziel.
 * Wer schon oben steht, braucht keinen Knopf dorthin.
 *
 * **Die Beschriftungen sind Kategorien, keine Kurzfassungen der Überschrift.**
 * «Ansatz» statt «Unser Ansatz», «Kontakt» statt «Fangen wir an»: Als Augenbraue
 * über «Unser Ansatz — dort automatisieren, wo Fehler teuer sind» läse sich ein
 * Etikett, das mit denselben Worten beginnt, als Stotterer. Ein Kategoriename
 * ordnet ein, statt zu wiederholen — und in der Leiste sucht der Besucher
 * ohnehin nach «Kontakt», nicht nach dem Wortlaut unserer Überschrift.
 *
 * Der Auffangname greift je Eintrag, nicht für die ganze Liste — dieselbe Regel
 * wie auf `/angebote`: Wer im MDX vier von sechs Beschriftungen pflegt, bekommt
 * für die übrigen zwei die deutschen Vorgaben statt leerer Knöpfe.
 */
const ANCHOR_NAV_FALLBACK = [
    { id: "plattform", label: "Die Plattform" },
    { id: "lage", label: "Ihre Lage" },
    // «Unser Ansatz» und «Referenzen» statt «Ansatz» und «Belege» (Entscheid
    // Jan, 16.9.2026).
    //
    // ⚠️ «Referenzen» steht auch im HAUPTMENUE, wo es auf `/referenzen` fuehrt.
    // Die zwei Eintraege sitzen wenige Pixel uebereinander und zeigen
    // verschieden weit: der Menuepunkt auf die Uebersichtsseite, dieser auf das
    // Kapitel dieser Seite. Das ist gewollt — der Besucher sucht dasselbe, die
    // Sprungleiste liefert den naechstgelegenen Treffer.
    { id: "ansatz", label: "Unser Ansatz" },
    { id: "beweis", label: "Referenzen" },
    { id: "wer-wir-sind", label: "Über uns" },
    { id: "anfangen", label: "Kontakt" },
] as const;

/**
 * The homepage, composed as the eight sections decided in Konzept § 5 (15.7.2026).
 * Each section has exactly one job and one exit; the order encodes two visitor
 * paths — the warm contact runs 1→3→7→8, the problem-driven visitor docks at 2 or 5.
 *
 * **Kapitel statt Sektionen (12.8.2026).** Gebaut waren vierzehn Sektionen, nicht
 * acht. Keine der sechs Ergaenzungen war fuer sich falsch; ihre Summe war es. Der
 * Weg zurueck geht ueber Zusammenfassen, nicht ueber Streichen — kein Inhalt
 * verschwindet, verwandte Sektionen ruecken unter eine gemeinsame `h2`
 * (`ChapterSection`). Vier Fusionen, und die Seite steht wieder auf den acht
 * Kapiteln, die § 5 beschlossen hat:
 *
 *   1 Hero (+ Mat) · 2 Quatico-Assistent · 3 In welcher Lage sind Sie gerade?
 *   4 Unser Ansatz (problem + howWeWork) · 5 Beweis (trust + successStories +
 *   relatedStories + portfolioCarousel) · 6 Whitepaper · 7 Wer wir sind
 *   (aboutTeaser + blogCarousel) · 8 Fangen wir an (closingCta + contact)
 *
 * Keine Kapitelueberschrift ist neu getextet: es ist jeweils der Titel des
 * ERSTEN Teils, nach oben gezogen. Plan:
 * docs/plans/2026-08-12-startseite-kapitel.md
 *
 * The primary CTA (KI-Potenzialanalyse) appears exactly twice: in the header and
 * in the closing block. Sections in between exit through their own links.
 *
 * The «Mat» KI-Berater-Störer in the hero is a deliberate GL deviation (24.7.)
 * from the pure eight-section § 5 structure: a hero corner element § 5 did not
 * foresee, added back as a second AI proof next to the Quatico-Assistent (two
 * AI systems, two proofs — not a redundancy). It links to the interactive Mat
 * advisor on /dienstleistungen/ki-technologie#berater.
 */
/**
 * Varianten-Vergleich fuer den VR (1.9.2026): `pageKey` erlaubt eine zweite und
 * dritte Startseiten-Fassung aus eigenen MDX-Dateien, ohne das Schema zu
 * aendern — alle Sektionen sind bereits optional. Ohne Prop bleibt es die
 * bisherige Startseite.
 */
type HomepageComponentProps = {
    pageKey?: string;
    /**
     * Schiebt den Assistenten hinter die drei Kaufanlaesse (Variante A,
     * 2.9.2026). Grund: Der Hero gibt eine Antwort, und die naechste
     * Ueberschrift fragte «Wie koennen wir Ihnen helfen?» — eine Rueckfrage an
     * jemanden, dem gerade etwas versprochen wurde. Sie stand ausserdem VOR
     * der Identitaet und VOR den Kaufanlaessen, also vor den beiden Fragen,
     * die ein Leser zuerst stellt.
     *
     * Als Prop und nicht als Umbau: Die Reihenfolge steht im Template, ein
     * Verschieben traefe sonst auch die LIVE-Startseite.
     */
    assistantAfterCornerstones?: boolean;
    /** Zweite Titelzeile in Markenfarbe statt kleiner, Titel groesser (Variante B, WorkOS-Muster). */
    heroHighlightSecondLine?: boolean;
};

export const Homepage: React.FC<HomepageComponentProps> = ({
    pageKey = "homepage",
    assistantAfterCornerstones = false,
    heroHighlightSecondLine = false,
}) => {
    const { getHeader, getFooter, getSectionContent, getPageContent } = useContent();
    const location = useLocation();

    // Mat 3D (Plan 2026-10-02-mat-3d-website): `mat_exposure` je Aufruf, Variante immer `3d`.
    useMatExposure(location.pathname);
    // 3D-Insel im Mat-Slot: legt im Browser eine Leinwand ueber die SVG (Ladebedingungen und Rueckfaelle in der Insel).
    const matSlotRef = React.useRef<HTMLDivElement>(null);
    useMat3dIsland(matSlotRef);

    const content = getPageContent<HomepageProps>(pageKey);
    const header = getHeader(content?.header);
    const footer = getFooter(content?.footer);

    // Homepage-specific overrides merged over the global section content.
    const trustContent = getSectionContent("trust", content?.trust);

    // Kontakt (v0.3 F5): dieselbe Naht wie `trust` — die globale
    // Kontakt-Sektion liefert Person, Bild und Kalender, die Startseite
    // ueberschreibt nur Titel und Text. So fuehrt die Startseite denselben
    // Ansprechpartner wie der Rest der Website, ohne ihn zu wiederholen.
    const contactContent = getSectionContent("contact", content?.contact);

    // Mat-Störer (hero): loaded straight from the global mat-teaser section file —
    // there is no page-level override (Mechanism A, no matTeaser field on
    // HomepageProps), so the override argument is undefined by design.
    // NOTE: the section key is camelCased — LanguageContext maps section ids to keys
    // via kebab→camel (LanguageContext.tsx:160), so the "mat-teaser" MDX id lives under
    // "matTeaser". "trust"/"contact" work with their bare id only because single-word
    // ids are kebab==camel; mat-teaser is the first multi-word section loaded this way.
    const matTeaserContent = getSectionContent<SectionMatTeaserProps>("matTeaser", undefined);
    const matTeaserHref = useLocalizedUrl("/dienstleistungen/ki-technologie#berater");

    // Eine Quelle fuer die Leiste UND die Kapitel-Etiketten: Zwei Felder liefen
    // auseinander, sobald jemand nur eins nachzieht.
    const anchorNavItems = ANCHOR_NAV_FALLBACK.map(fallback => ({
        id: fallback.id,
        label: content?.anchorNav?.find(item => item.id === fallback.id)?.label ?? fallback.label,
    }));
    const kapitelEtikett = (id: string) => anchorNavItems.find(item => item.id === id)?.label;

    // Title/description come from the per-language SEO content so the English
    // homepage gets its English metadata. Kein Literal als Rueckfall: Der fing
    // frueher nichts ab, sondern setzte deutschen Text auf die englische Seite
    // (A1, 12.8.2026 — `literal-statt-content.spec.ts` haelt das offen).
    // `useHead` hat fuer den Ausnahmefall einen sprachbewussten Standard.
    // Organization + WebSite JSON-LD are injected site-wide by GlobalStructuredData
    // (AppLayout) — repeating them here would emit the same entity twice.
    const seo = content?.seo;
    useHead({
        title: seo?.metaTitle || seo?.title,
        description: seo?.metaDescription,
        keywords: seo?.metaKeywords,
        ogImage: "/images/og-home.jpg",
        canonical: location.pathname || "/",
    });

    if (!content) {
        console.error("Homepage: Missing content", {
            content: !!content,
            header: !!header,
        });
        return null;
    }

    if (!content.hero) {
        console.error("Homepage: Missing hero content");
        return null;
    }

    if (!header) {
        console.error("Homepage: Missing navigation");
        return null;
    }

    return (
        <div className="min-h-screen bg-white">
            {header && <Header {...header} />}

            <main>
                {/* 1 — Hero. Job: confirm. The primary CTA #1 lives in the header; the
                    hero's own button is deliberately `secondary` (content-set) so it
                    supports the assistant below it instead of competing with it — the
                    assistant is this page's primary action.
                    The Mat-Störer overlays the hero's top-right corner (GL 24.7.,
                    second AI proof). HeroSection renders `children` at section level
                    (see HeroSection.tsx), i.e. as a sibling of its own content
                    container — so we mirror that container's width/padding here
                    (`container mx-auto px-6 md:px-12 lg:px-16`) to anchor the Störer
                    to the CONTENT's right edge, not the full-bleed section/viewport.
                    The wrapper is `h-0` (adds no layout height) with a `top-*` offset
                    that, combined with MatTeaserBadge's own negative `-top-8/-top-12`,
                    lands the speech bubble below the nav, level with the hero image.

                    Mobile (< md) is a SINGLE column: title/subtitle/description
                    stack above the hero image, so the desktop `top-40` would land
                    the Störer directly on the title/subtitle text. Measured at a
                    390px mobile viewport: h1 ends at ~327px, subtitle at ~437px,
                    description at ~538px from the section top. MatTeaserBadge lifts
                    its speech bubble `-top-8` (32px) above this wrapper's anchor,
                    so the anchor must sit at description-bottom + gap + 32px ≈
                    600px for the bubble to clear the description with room to
                    spare. `top-[38rem]` (608px) does that, landing the Störer on
                    the hero image itself — matching the live-site placement
                    (below the text, over the image). The `md:top-48` override is
                    untouched, so ≥ md keeps the original top-right-of-image
                    placement exactly as before. */}
                <HeroSection
                    layout="two-column"
                    background="gradient"
                    title={content.hero.title}
                    subtitle={content.hero.subtitle}
                    description={content.hero.description}
                    highlightSecondLine={heroHighlightSecondLine}
                    oversizedTitle={heroHighlightSecondLine}
                    ctaButtons={content.hero.ctaButtons}
                    /* Das wandernde Bausteine-Band ersetzt die Illustration,
                       sobald der Content ein `band` fuehrt (12.9.2026, Vorbild
                       workos.com). Ohne `band` bleibt alles wie bisher — die
                       heutige Startseite rendert unveraendert. */
                    visual={
                        content.plattformModule?.band ? (
                            <BausteinKarussell
                                /* `mt-24` schiebt das Band unter den Mat-Teaser.
                                   Nachgemessen 12.9.2026: Mat reicht bis y=394,
                                   das Band begann bei y=324 — 70px Ueberschneidung
                                   genau auf der obersten Zeile. Mat ist absolut
                                   positioniert und weicht nicht aus. */
                                /* `hidden md:block`: Unter 768px stapelt der Hero,
                                   und das Band stuende UNTER dem Text statt daneben
                                   — 416 px hoch, also die halbe Bildschirmhoehe,
                                   ohne den Satz zu illustrieren, zu dem es gehoert.
                                   Bei 768px steht es nachgemessen sauber neben dem
                                   Text (360 px breit, keine Zeile bricht um).
                                   Entscheid Jan, 14.9.2026: nur zeigen, wo neben dem
                                   Hero-Text Platz ist.

                                   `mt-[10.5rem]` haelt das Band unter Mat. Eine
                                   fruehere Fassung stand auf `mt-24` und war 71 px
                                   zu hoch: Mat reicht bis y=399, das Band begann bei
                                   y=328, und weil Mat `z-20` traegt, verdeckte er
                                   DREI Zeilen mit je 275 px.

                                   ⚠️ Die damalige Messung meldete «keine
                                   Ueberlappung» — sie prueft die erste LISTENZEILE,
                                   und die stand ausserhalb des sichtbaren Bereichs.
                                   Massgeblich ist der Bandrahmen, nicht ein
                                   einzelnes Element darin.

                                   `max-w-xl ml-auto`: Ohne Grenze wuchs das Band
                                   mit der Hero-Spalte — nachgemessen 8.10.2026:
                                   624 px bei 1280, 773 px ab 1680, die aktive
                                   Zeile 681 px breit. 36rem (576 px) entspricht
                                   ungefaehr der Breite bei 1280; `ml-auto` haelt
                                   das Band rechts unter Mat, statt es in die
                                   Spaltenmitte rutschen zu lassen. Unter 1280 ist
                                   die Spalte schmaler als die Grenze, dort aendert
                                   sich nichts (768: 360 px, 1024: 475 px). */
                                className="mt-[10.5rem] hidden md:block max-w-xl ml-auto"
                                items={content.plattformModule.items.map(m => ({
                                    icon: m.icon,
                                    title: m.title,
                                    aktiv: (content.plattformModule?.band?.aktiv ?? []).includes(m.title),
                                }))}
                                labelAktiv={content.plattformModule.band.labelAktiv}
                                labelBereit={content.plattformModule.band.labelBereit}
                                ariaLabel={content.plattformModule.band.ariaLabel}
                            />
                        ) : undefined
                    }
                    image={content.hero.image}
                    imageAlt={content.hero.imageAlt || ""}
                    imageClassName="origin-left mt-4 -mb-8 md:mt-8 md:mb-0 md:scale-110 lg:scale-100 xl:scale-90"
                >
                    {matTeaserContent && (
                        /* Der Halter fuellt den Hero (`absolute inset-0`), statt `h-0`
                           zu sein. Nur so hat `bottom` einen Bezug: Die Hero-Hoehe
                           schwankt mit der Zeilenzahl des Textes — nachgemessen
                           14.9.2026 sind es 882 px auf der Startseite, aber 749 px
                           bei Variante B und 604 px bei 744 px Breite. Jeder feste
                           `top`-Wert passt deshalb nur zu EINER Seite: `top-[38rem]`
                           sass auf der Startseite richtig und ragte bei Variante B
                           74 px in die naechste Sektion; ein daran angepasster Wert
                           brach dafuer die Startseite.

                           `pointer-events-none` haelt den Halter durchlaessig, damit
                           er den Hero nicht abdeckt; Mat bekommt die Klicks per
                           `pointer-events-auto` zurueck. Waagrechte Ausrichtung wie
                           zuvor am Inhaltsrand (`container mx-auto px-6 …`). */
                        <div className="pointer-events-none absolute inset-0">
                            <div className="container relative mx-auto h-full px-6 md:px-12 lg:px-16">
                                {/* ⚠️ `MatTeaserBadge` positioniert sich SELBST mit
                                    `absolute -top-8` (bzw. `md:-top-12`) und haengt
                                    damit an der OBERkante dieses Halters. Der Halter
                                    braucht deshalb eine Hoehe — ohne sie faellt die
                                    Sprechblase auf seine Oberkante, und `bottom` hat
                                    keine Wirkung (nachgemessen 14.9.2026: Mat ragte
                                    182 px aus dem Hero).

                                `h-[10rem]` entspricht der Figur ohne Sprechblase
                                    (155 px, die Blase entfaellt unter `md` — siehe
                                    `MatTeaserBadge`); mit `bottom-6` endet Mat damit
                                    knapp ueber der Hero-Kante, unabhaengig davon, ob
                                    der Hero 882, 749 oder 604 px hoch ist. */}
                                {/* ⚠️ **Unterhalb `md` faengt der Teaser KEINE Klicks**
                                    (`pointer-events-none md:pointer-events-auto`).
                                    Bei 375 px liegt er ueber beiden Hero-Knoepfen:
                                    nachgemessen 21.9.2026 ueberlappt der Link-Kasten
                                    «Mit uns sprechen» und «Die Plattform ansehen» je
                                    144 x 56 px, der Cyan-Schein (`.mat-glow`) 141 px
                                    breit, der Ei-Koerper selbst 58 x 54 px.
                                    «Die Plattform ansehen» war dadurch tot —
                                    `elementFromPoint` in der Knopfmitte lieferte die
                                    Glow-Ellipse; «Mit uns sprechen» ueberlebte nur,
                                    weil seine Mitte zufaellig links neben Mat faellt.

                                    Die Sperre sitzt HIER und nicht in `MatFigure`
                                    oder `MatTeaserBadge`: nachgemessen blockiert jede
                                    Ebene einzeln weiter. Nur das SVG durchlaessig zu
                                    machen laesst den `<a>` blockieren, beide zusammen
                                    laesst diesen Halter blockieren — er ist das
                                    Element, das sich das `pointer-events-auto`
                                    ueberhaupt erst zurueckholt.

                                    Der Verlust ist klein: unter `md` entfaellt die
                                    Sprechblase (siehe `MatTeaserBadge`), Mat ist dort
                                    reine Dekoration ohne sichtbares Angebot. Ab `md`
                                    sitzt er rechts neben dem Text, ueberlappt nichts
                                    und bleibt mitsamt Blase klickbar. */}
                                <div
                                    ref={matSlotRef}
                                    className="pointer-events-none absolute bottom-6 right-6 z-20 h-[10rem] w-[210px] md:bottom-auto md:right-12 md:top-48 md:h-auto md:w-[240px] md:pointer-events-auto lg:right-16"
                                >
                                    <MatTeaserBadge
                                        href={matTeaserHref}
                                        question={matTeaserContent.question}
                                        ctaLabel={matTeaserContent.ctaText}
                                        ariaLabel={matTeaserContent.ariaLabel}
                                        badgeLabel={matTeaserContent.badgeLabel}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </HeroSection>

                {/*
                    Sprungnavigation. Die Startseite ist die laengste Seite der
                    Website; wer wissen wollte, ob weiter unten Belege oder ein
                    Ansprechpartner stehen, musste sie ganz abrollen.

                    **Ueber dem Assistenten, nicht darunter (Entscheid Jan,
                    13.8.2026).** Am 12.8. stand die Leiste HINTER dem
                    Assistenten, mit der Begruendung, ein Inhaltsverzeichnis
                    gehoere hinter den Einstieg aus Hero und Assistent —
                    dieselbe Stelle wie auf `/angebote` und den Workshop-Seiten.

                    Der Assistent ist aber kein Einstieg wie ein Hero, sondern
                    ein Angebot, das Zeit kostet: Wer ihn benutzt, tippt eine
                    Frage und wartet auf eine Antwort. Diese Entscheidung
                    verlangt zu wissen, was die Seite sonst noch bietet — genau
                    das leistet die Leiste. Darunter erreicht sie nur, wer den
                    Assistenten schon uebersprungen hat.

                    Die Analogie zu `/angebote` traegt hier nicht: Dort folgt auf
                    den Hero unmittelbar Inhalt, keine Interaktion.

                    `colorScheme="primary"`, nicht `accent`: Der Akzent gehoert
                    dem Vertriebsportal mit seinen Editionsfarben. */}
                <VpStickySectionNav items={anchorNavItems} />

                {/*
                    2 — Das Kapitel «Die Plattform» (16.9.2026, Entscheid Jan).
                    Job: die Zusage des Heros begruenden, BEVOR der Besucher sich
                    zuordnet — und ihn gleich fragen lassen, was er darauf gebaut
                    haben will.

                    Warum hier und nicht an Position 7: Der Hero verspricht «in
                    30 Tagen startklar» und zeigt rechts das Baustein-Band. Die
                    Erklaerung dazu stand bis zum 16.9. im Kapitel «Unser Ansatz»
                    — wer die Zusage nicht glaubte, las bis dahin nicht.

                    Warum Plattform UND Assistent in einem Kapitel: Der Teaser
                    sagt «bringen Sie Ihre Fachlichkeit», der Assistent direkt
                    darunter ist die Stelle, an der genau das geschieht. Zwei
                    Sektionen, ein Gedanke.

                    EINES von beiden genuegt, nicht der Teaser allein. Die erste
                    Fassung vom 16.9. haengte das ganze Kapitel an
                    `content.plattformNutzen` — dann verschwand der Assistent
                    lautlos mit, obwohl er ein eigenes Content-Feld hat. Vier
                    Startseiten-Waechter meldeten es am selben Tag (Fixture ohne
                    `plattformNutzen`, `assistant` fiel aus der gerenderten
                    Folge). Der Kapiteltitel kommt weiter aus dem Teaser; fehlt
                    er, traegt das Kapitel nur sein Etikett aus `anchorNav`.
                */}
                {(content.plattformNutzen || (content.assistant && !assistantAfterCornerstones)) && (
                    <ChapterSection
                        id="plattform"
                        badge={kapitelEtikett("plattform")}
                        title={content.plattformNutzen?.title}
                        background="light"
                    >
                        {content.plattformNutzen && <PlattformNutzenSection {...content.plattformNutzen} nested />}
                        {content.assistant && !assistantAfterCornerstones && (
                            <AssistantSection {...content.assistant} nested />
                        )}
                    </ChapterSection>
                )}

                {/* 3 — Three cornerstone cards. Job: branch, one path per card.
                    Kein `ChapterSection` darum: Die Sektion bringt Kopf und
                    dunklen Ton selbst mit, ein Gefaess ergaebe eine zweite
                    Ueberschrift. Sprungziel und Etikett bekommt sie trotzdem. */}
                {content.cornerstones && (
                    <CornerstonesSection {...content.cornerstones} anchorId="lage" badge={kapitelEtikett("lage")} />
                )}

                {content.assistant && assistantAfterCornerstones && <AssistantSection {...content.assistant} />}

                {/*
                    4 — Das Kapitel «Unser Ansatz» (12.8.2026). Job: den kalten,
                    problemgetriebenen Besucher andocken — und gleich sagen, wie
                    gearbeitet wird.

                    Zwei Absaetze, ZWEI Ausgaenge: /angebote und
                    /agentic-development. Beide bleiben. Ein Kapitel mit zwei
                    Ausgaengen ist zulaessig, ein verlorener Ausgang waere ein
                    Fehler.

                    Der erste Absatz sagt WAS wir bauen (bestimmt statt Chatbot),
                    der zweite WIE (Ingenieure mit Agenten). Das war die
                    schwaechste der vier Fusionen und stand unter Vorbehalt: Liest
                    es als Brei, bleibt es bei zwei Sektionen. Am Artefakt
                    nachgesehen traegt es — «Unser Ansatz» ist ein Dach, unter das
                    «So arbeiten wir» als Unterkapitel passt. */}
                {content.problem && (
                    <ChapterSection
                        id="ansatz"
                        badge={kapitelEtikett("ansatz")}
                        title={content.problem.title}
                        background="light"
                    >
                        <ProblemSection {...content.problem} nested />
                        {content.howWeWork && <HowWeWorkSection {...content.howWeWork} nested />}
                        {/*
                            Dritter Teil seit dem 16.9.2026 (Entscheid Jan): die
                            Denkleistung. Sie stand bis dahin nach den Belegen,
                            begruendet als «der Beweis, der kein Kundenfall ist —
                            erst der Beweis, dann die Frage nach dem Termin»
                            (11.8.2026).

                            Jetzt gehoert sie zum Ansatz: Ein Whitepaper zeigt,
                            WIE gedacht wird, nicht DASS es funktioniert hat. Der
                            eigene Anker `#whitepaper` faellt damit weg — das
                            Kapitel traegt sie mit.

                            Textlink auf /smart-client-relations: Die Seite trug
                            das Whitepaper-Formular und war zuvor von keiner
                            Seite erreichbar.
                        */}
                        {content.thoughtLeadership && (
                            <ThoughtLeadershipSection {...content.thoughtLeadership} nested />
                        )}
                        {/*
                            Dritter Teil (14.8.2026): WAS wir bauen, WIE wir es
                            bauen — und WORAUF. Loest USP-Saeule 2 ein
                            («Integration fast gratis»), die als einzige der drei
                            Saeulen auf der Startseite fehlte, waehrend Saeule 1
                            hier darueber und Saeule 3 im Beweis-Kapitel steht.

                            Der dritte Ausgang ist zulaessig — siehe oben: ein
                            Kapitel mit mehreren Ausgaengen ist in Ordnung, ein
                            verlorener Ausgang waere der Fehler. Er fuehrt auf
                            /plattform, wo die Saeule ausgefuehrt ist.

                            Ausloeser: Kundenfeedback nach Release. Befund:
                            docs/befunde/2026-08-14-technologie-usp-nach-release.md
                        */}
                        {/*
                            Das Kachelraster stand hier bis 14.9.2026 und ist
                            entfallen (Entscheid Jan): Es nannte dieselben zwoelf
                            Namen wie das Band im Hero ein zweites Mal und trug
                            2734 px zur Seitenlaenge auf dem Telefon bei — ein
                            Sechstel der Gesamthoehe fuer zwoelf einspaltige
                            Kacheln.

                            Die Beschreibungen sind NICHT verloren: Sie stehen
                            weiter in `home.mdx` unter `plattformModule.items`
                            (bis zum 15.9.2026 `home-variante-b.mdx`) und wandern
                            als Naechstes auf die Plattform-Seiten. `PlattformModuleSection` und das
                            Schema bleiben deshalb bestehen.
                        */}
                    </ChapterSection>
                )}

                {/*
                    5 — Das Beweis-Kapitel (12.8.2026). Job: belegen, dass das
                    Vorstehende gebaut ist — und zwar EINMAL.

                    Bis hierher sagten vier getrennte Sektionen dasselbe: die
                    Kundenlogos (Position 3), MeteoSchweiz (7), «Vier weitere Faelle»
                    (8) und das Portfolio-Karussell (12). Jede war einzeln begruendet;
                    zusammen las der Besucher viermal *das haben wir gebaut, fuer die*,
                    verteilt ueber die halbe Seite. Es ist derselbe Befund wie auf
                    `/referenzen` am 11.8.: dort standen Erfolgsgeschichten und
                    Referenzen auf zwei Ebenen, hier waren es vier.

                    Reihenfolge im Kapitel: vom Breitesten zum Konkretesten — zwanzig
                    Logos, ein ausformulierter Fall mit Zitat, vier weitere Faelle als
                    Kacheln, dann das laufende Portfolio.

                    Der Kapiteltitel ist die Trust-Ueberschrift, nach oben gezogen:
                    «Software, auf die sich 2.5 Millionen Menschen verlassen» ist die
                    staerkste Zeile, die die Seite hat, und traegt das ganze Kapitel.
                    Der Plan schliesst neue Sektionstexte aus — also wird umgehaengt,
                    nicht gedichtet. `TrustSection` rendert im `nested`-Modus deshalb
                    nur noch das Logoband. */}
                {trustContent && (
                    <ChapterSection
                        id="beweis"
                        badge={kapitelEtikett("beweis")}
                        title={trustContent.title}
                        description={trustContent.description}
                    >
                        <TrustSection {...trustContent} nested />

                        {/* Bewusst nicht fuenf gleichrangige Kacheln: ein ausformulierter
                            Fall mit Zitat wirkt, fuenf Kacheln liest niemand (3.8.2026,
                            Entscheid Jan — seither MeteoSchweiz statt ewz). Die uebrigen
                            vier folgen ohne eigene Ueberschrift direkt darunter. */}
                        {content.successStories?.items?.[0] && (
                            <SuccessStorySection
                                title={content.successStories.title}
                                story={content.successStories.items[0]}
                                nested
                            />
                        )}

                        {content.relatedStories && <RelatedStoriesSection {...content.relatedStories} nested />}

                        {/* Die Karten kommen aus der portfolio-Collection, nicht aus dem
                            MDX. Ausgang: /referenzen. */}
                        {content.portfolioCarousel && (
                            <PortfolioCarouselSection {...content.portfolioCarousel} nested />
                        )}
                    </ChapterSection>
                )}

                {/*
                    7 — Das Kapitel «Wer wir sind» (12.8.2026). Job: das Haus
                    zeigen, bevor die Seite endet.

                    «Wer bei uns anruft, spricht mit jemandem, der mitentscheidet»
                    und «Technik, Auszeichnungen, und Wochenenden, die nichts mit
                    Software zu tun haben» beantworten dieselbe Frage. Getrennt
                    standen sie als zwei Abschnitte mit je eigenem Badge; jetzt
                    steht der Blog als Beleg unter der Aussage.

                    Zwei Ausgaenge, beide bleiben: /ueber-uns und /blog. */}
                {content.aboutTeaser && (
                    <ChapterSection
                        id="wer-wir-sind"
                        badge={kapitelEtikett("wer-wir-sind")}
                        title={content.aboutTeaser.title}
                    >
                        <AboutTeaserSection {...content.aboutTeaser} nested />
                        {content.blogCarousel && <BlogCarouselSection {...content.blogCarousel} nested />}
                    </ChapterSection>
                )}

                {/*
                    8 — Das Kapitel «Fangen wir an» (12.8.2026). Job: umwandeln —
                    mit einem Namen daneben.

                    Bis hierher standen der Abschluss-Aufruf (Position 10) und die
                    Kontaktperson (14) getrennt, mit «Wer wir sind» und dem Blog
                    dazwischen. Auf `/referenzen` war am 11.8. genau das der
                    Befund: ein Handlungsaufruf ohne Namen, ohne Telefonnummer und
                    ohne Terminlink. Zwei bezahlte Angebote und der Mensch, den man
                    stattdessen einfach fragen kann, gehoeren nebeneinander.

                    Das Kapitel traegt den Farbverlauf, damit die Seite auf EINEM
                    Block endet statt auf zweien. Deshalb `onDark` am
                    Abschluss-Block: seine Knoepfe standen bisher auf hellem Grund.

                    ⚠️ Zwei Anker, mit Absicht:
                    - `#anfangen` am Kapitel — Sprungziel der Anker-Nav (Etappe 4).
                    - `#kontakt` an der Kontakt-Sektion, NICHT am Kapitel. Der Link
                      soll bei einem Menschen landen, nicht bei zwei bezahlten
                      Angeboten (v0.3 F5). Deshalb traegt der Abschluss-Block
                      weiterhin `anchor={false}` — sonst stuende die id zweimal im
                      selben Kapitel, ungueltiges HTML und ein stiller Fehlsprung. */}
                {content.closingCta && (
                    <ChapterSection
                        id="anfangen"
                        badge={kapitelEtikett("anfangen")}
                        title={content.closingCta.title}
                        description={content.closingCta.description}
                        background="gradient"
                    >
                        <ClosingCtaSection {...content.closingCta} anchor={false} nested onDark />
                        {contactContent && <ContactSection {...contactContent} anchor={true} nested />}
                    </ChapterSection>
                )}
            </main>

            {footer && <Footer {...footer} />}
        </div>
    );
};

export default Homepage;
